// The wcBot's per-decision caches: zone layouts (numbered), route maps and strike areas, shared by
// every position scored for one decision. Pure helpers over bot/movement.ts and bot/routes.ts.
//
// The positions of one decision differ mostly in where the unit being tried (the mover) ends up. So
// that they share maps and strike areas, the mover's zone is left out of the zones that stop the
// enemy, with one exception: an enemy carrier's route home is searched exactly, with every zone
// (blocking it is too important to approximate).

import { zoneUp, other, moveOf } from '../../rules';
import { hasTrait } from '../../units';
import type { CtfState, Team, Unit } from '../../types';
import { hid, newStamp, onMapTable, reachFrom, stopsFor, zoneOffsets, zoneSet, NO_STOPS } from '../movement';
import { distanceMap, forwardTurns, lookup, MAX_ACTIVATIONS } from '../routes';
import type { DistanceMap } from '../routes';

export interface Ctx {
  layouts: Map<string, number>;       // zone layout signature → id
  zones: Set<number>[];               // id → hexes covered
  maps: Map<string, DistanceMap>;
  strikes: Map<number, Set<number>>;
  turns: Map<string, number>;         // exact enemy carrier routes
  mover: string | null;               // the unit being tried
}
export const newCtx = (): Ctx => ({ layouts: new Map(), zones: [], maps: new Map(), strikes: new Map(), turns: new Map(), mover: null });

// One position, with its zone layouts looked up: upId/allId without the mover, full() with it.
export interface Pos { s: CtfState; ctx: Ctx; areas: (Set<number> | undefined)[]; upId: Record<Team, number>; allId: Record<Team, number>; full: (onlyUp: boolean, t: Team) => number; moverTeam: Team | null }

export function posOf(s: CtfState, ctx: Ctx): Pos {
  const mi = ctx.mover ? s.units.findIndex(u => u.id === ctx.mover) : -1;
  const layout = (t: Team, onlyUp: boolean, skip: number) => {
    let sig = onlyUp ? 'u' : 'a';
    for (let i = 0; i < s.units.length; i++) {
      const u = s.units[i];
      if (i !== skip && u.team === t && u.pos && !u.inPrison && (!onlyUp || zoneUp(s, u))) sig += `${i}:${hid(u.pos) * 8 + u.facing},`;
    }
    let id = ctx.layouts.get(sig);
    if (id === undefined) {
      id = ctx.zones.length;
      ctx.layouts.set(sig, id);
      ctx.zones.push(zoneSet(s, t, onlyUp, skip));
    }
    return id;
  };
  const moverTeam = mi >= 0 ? s.units[mi].team : null;
  const ids = (onlyUp: boolean, skip: number) => ({ A: layout('A', onlyUp, moverTeam === 'A' ? skip : -1), B: layout('B', onlyUp, moverTeam === 'B' ? skip : -1) });
  const upId = ids(true, mi), allId = ids(false, mi);
  const full = (onlyUp: boolean, t: Team) => (t === moverTeam ? layout(t, onlyUp, -1) : (onlyUp ? upId : allId)[t]);
  return { s, ctx, areas: [], upId, allId, full, moverTeam };
}

// Whether u's team can still activate it this round.
export const freshThisRound = (s: CtfState, u: Unit) => !u.activated && s.activations[u.team].used < s.activations[u.team].allowed;

// Activations unit u needs to stand on one of `targets` (0: already there; MAX_ACTIVATIONS + 1: not
// within reach). Its first activation is stopped by the enemy zones up now (if it can still act this
// round), later ones by every enemy zone. `key` names the target set.
export function routeTurns(p: Pos, u: Unit, targets: number[], key: string): number {
  if (!u.pos) return MAX_ACTIVATIONS + 1;
  const { s, ctx } = p;
  const e = other(u.team), move = moveOf(s, u), slip = hasTrait(s, u, 'slippery');
  const thisRound = freshThisRound(s, u);
  if (u.carrying && e === p.moverTeam && !slip) {
    const start = hid(u.pos) * 8 + u.facing;
    const up = thisRound ? p.full(true, e) : -1, all = p.full(false, e);
    const tk = `${start}|${move}|${up}|${all}|${key}`;
    let t = ctx.turns.get(tk);
    if (t === undefined) {
      t = targets.includes(start >> 3) ? 0 : forwardTurns(onMapTable(s), move, start, new Set(targets), ctx.zones[thisRound ? up : all], ctx.zones[all]);
      ctx.turns.set(tk, t);
    }
    return t;
  }
  const mk = `${key}|${move}|${slip ? 's' : `${p.upId[e]}/${p.allId[e]}`}`;
  let dm = ctx.maps.get(mk);
  if (!dm) {
    dm = distanceMap(onMapTable(s), move, targets, slip ? NO_STOPS : ctx.zones[p.upId[e]], slip ? NO_STOPS : ctx.zones[p.allId[e]]);
    ctx.maps.set(mk, dm);
  }
  return lookup(dm, hid(u.pos) * 8 + u.facing, thisRound);
}

// The hexes unit i could tag into on its next activation: its zone from every state it can reach
// (stopped by the other team's up zones), or from where it stands if it can't move.
export function strikeArea(p: Pos, i: number): Set<number> {
  const known = p.areas[i];
  if (known) return known;
  const { s, ctx } = p;
  const u = s.units[i];
  const foe = other(u.team), slip = hasTrait(s, u, 'slippery');
  const active = s.active?.unitId === u.id;
  const moves = active ? (s.active!.moveLocked ? 0 : s.active!.movesLeft) : moveOf(s, u);
  const start = hid(u.pos!) * 8 + u.facing;
  const k = ((i * 32768 + start) * 16 + moves) * 65536 + (slip ? 65535 : p.upId[foe]);
  let out = ctx.strikes.get(k);
  if (!out) {
    out = new Set<number>();
    const stops = stopsFor(s, u, ctx.zones[p.upId[foe]]);
    const offs = [0, 1, 2, 3, 4, 5].map(f => zoneOffsets(s, u, f));
    for (const st of [start, ...reachFrom(onMapTable(s), moves, stops, [start], newStamp())]) {
      const h = st >> 3;
      for (const d of offs[st & 7]) out.add(h + d);
    }
    ctx.strikes.set(k, out);
  }
  return (p.areas[i] = out);
}
